import { Link } from "react-router-dom";
import { ArrowUpRight, Check, Quote } from "lucide-react";
import SectionHeading from "@/components/shared/SectionHeading";
import ComparisonTable from "@/components/shared/ComparisonTable";
import FaqAccordion from "@/components/shared/FaqAccordion";
import OutlineMarquee from "@/components/shared/OutlineMarquee";
import CtaButton from "@/components/shared/CtaButton";
import DynamicIcon from "@/components/shared/DynamicIcon";
import { Reveal, Stagger, StaggerItem } from "@/components/motion/Reveal";
import { COMPARISON, ACRONYMS } from "@/data/comparisons";
import { PLATFORMS, PROCESS, WHY_GEO, WHY_CHOOSE } from "@/data/platforms";
import { SERVICES } from "@/data/services";
import { INDUSTRIES } from "@/data/industries";
import { HOME_FAQS } from "@/data/faqs";
import { STATS } from "@/data/site";

const SECTION = "max-w-7xl mx-auto px-6 lg:px-12 py-24 lg:py-32";

/* ---------------- What is GEO ---------------- */
export function WhatIsGeo() {
  return (
    <section id="what-is-geo" className="border-t border-gray-100">
      <div className={SECTION}>
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-16">
          <div className="lg:col-span-5">
            <SectionHeading eyebrow="Definition" title="What is Generative Engine Optimization?" />
          </div>
          <div className="lg:col-span-7">
            <Reveal>
              <div className="rounded-2xl border border-gray-200 bg-[#FAFAFA] p-8 mb-8" data-testid="geo-definition">
                <p className="text-lg leading-relaxed text-[#111111]">
                  <strong>Generative Engine Optimization (GEO)</strong> is the practice of optimizing a
                  brand&apos;s content, structured data, entities and digital authority so AI-powered engines —
                  ChatGPT, Google AI Mode, Gemini, Claude and Perplexity — can{" "}
                  <span className="text-[#0B63F6] font-semibold">discover, understand, trust and cite</span>{" "}
                  it inside generated answers. It is the successor to SEO for the era of AI search.
                </p>
              </div>
            </Reveal>
            <Reveal delay={0.1}>
              <p className="text-gray-600 leading-relaxed mb-8">
                As buyers move from typing queries into Google to asking AI assistants, the brands AI names
                and recommends win the market. GEO makes your business the answer.
              </p>
            </Reveal>
            <div className="grid sm:grid-cols-2 gap-4">
              {ACRONYMS.map((a, i) => (
                <Reveal key={a.term} delay={0.05 * i}>
                  <div className="rounded-xl border border-gray-200 p-5 h-full">
                    <div className="flex items-baseline gap-2">
                      <span className="font-display text-lg font-extrabold text-[#111111]">{a.term}</span>
                      <span className="text-xs text-gray-400">{a.full}</span>
                    </div>
                    <p className="text-sm text-gray-600 mt-2 leading-relaxed">{a.desc}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>

        <div className="mt-20">
          <SectionHeading eyebrow="Comparison" title="SEO vs GEO" sub="How generative engine optimization reframes the goals of search." className="mb-10" />
          <ComparisonTable headers={COMPARISON.headers} rows={COMPARISON.rows} />
        </div>
      </div>
    </section>
  );
}

/* ---------------- Why businesses need GEO ---------------- */
export function WhyGeo() {
  return (
    <section className="bg-[#FAFAFA] border-y border-gray-100">
      <div className={SECTION}>
        <SectionHeading
          eyebrow="The Shift"
          title="Why businesses need GEO now"
          sub="AI has changed how customers discover and choose brands. Here's what's at stake."
          className="mb-14"
        />
        <Stagger className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {WHY_GEO.map((item, i) => (
            <StaggerItem key={item.title}>
              <div className="h-full rounded-2xl border border-gray-200 bg-white p-8 hover:-translate-y-1 hover:shadow-[0_8px_30px_rgba(0,0,0,0.04)] transition-all duration-500">
                <div className="font-display text-sm font-bold text-[#0B63F6]">0{i + 1}</div>
                <h3 className="font-display text-xl font-bold tracking-tight mt-4">{item.title}</h3>
                <p className="text-gray-600 mt-3 leading-relaxed text-[15px]">{item.body}</p>
              </div>
            </StaggerItem>
          ))}
        </Stagger>
      </div>
    </section>
  );
}

/* ---------------- What we're hired for ---------------- */
const HIRED_FOR = [
  "GEO Strategy", "AI Visibility Audit", "Technical GEO", "Structured Data",
  "Entity Optimization", "Knowledge Graph", "AI Citation Strategy", "Semantic SEO",
  "AI Content Engineering", "AI Authority Building", "GEO Monitoring",
];
export function WhatWeDo() {
  return (
    <section>
      <div className={SECTION}>
        <div className="grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <SectionHeading
              eyebrow="Who We Are"
              title="We help brands become visible in AI search"
              sub="CompuBrain AI is a Generative Engine Optimization consulting and implementation partner — not a chatbot builder, not a generic marketing agency. Businesses hire us to be recommended by AI."
            />
            <Reveal delay={0.15}>
              <div className="mt-8">
                <CtaButton source="who-we-are" title="Talk to a GEO Expert" testId="whatwedo-cta">Talk to a GEO Expert</CtaButton>
              </div>
            </Reveal>
          </div>
          <div className="lg:col-span-7">
            <Stagger className="grid sm:grid-cols-2 gap-3">
              {HIRED_FOR.map((h) => (
                <StaggerItem key={h}>
                  <div className="flex items-center gap-3 rounded-xl border border-gray-200 px-5 py-4 hover:border-[#0B63F6] transition-colors">
                    <span className="h-6 w-6 rounded-full bg-[#0B63F6]/10 flex items-center justify-center shrink-0">
                      <Check className="h-3.5 w-3.5 text-[#0B63F6]" />
                    </span>
                    <span className="font-medium text-[#111111]">{h}</span>
                  </div>
                </StaggerItem>
              ))}
            </Stagger>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ---------------- Platforms ---------------- */
export function Platforms() {
  return (
    <section className="bg-[#111111] text-white overflow-hidden">
      <div className="pt-24 lg:pt-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <SectionHeading
            eyebrow="Coverage"
            title="Platforms we optimize for"
            sub="We engineer your visibility across today's leading AI engines — and the platforms defining tomorrow's search."
            light
            className="mb-14"
          />
        </div>
        <Stagger className="max-w-7xl mx-auto px-6 lg:px-12 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
          {PLATFORMS.map((p) => (
            <StaggerItem key={p.name}>
              <div className="h-full rounded-2xl border border-white/10 bg-white/[0.03] p-6 hover:bg-white/[0.06] hover:border-white/25 transition-all">
                <div className="font-display text-lg font-bold">{p.name}</div>
                <div className="text-xs text-[#71717A] mt-1">{p.vendor}</div>
                <p className="text-[13px] text-[#A1A1AA] mt-4 leading-relaxed">{p.note}</p>
              </div>
            </StaggerItem>
          ))}
        </Stagger>
      </div>
      <div className="mt-20 border-t border-white/10">
        <OutlineMarquee light words={["ChatGPT", "Gemini", "Claude", "Perplexity", "Copilot", "Grok", "Meta AI"]} />
      </div>
    </section>
  );
}

/* ---------------- Services grid ---------------- */
export function ServicesGrid() {
  const list = SERVICES.slice(0, 8);
  return (
    <section id="services">
      <div className={SECTION}>
        <div className="flex flex-wrap items-end justify-between gap-6 mb-14">
          <SectionHeading eyebrow="Our GEO Services" title="A complete GEO capability" sub="Strategy, engineering and authority — everything needed to win AI recommendation." />
          <Reveal><Link to="/services" data-testid="services-view-all" className="text-sm font-medium text-[#0B63F6] hover:text-[#111111] transition-colors inline-flex items-center gap-1">View all services <ArrowUpRight className="h-4 w-4" /></Link></Reveal>
        </div>
        <Stagger className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
          {list.map((s) => (
            <StaggerItem key={s.slug}>
              <Link to={`/services/${s.slug}`} data-testid={`service-card-${s.slug}`} className="group block h-full rounded-2xl border border-gray-200 p-7 hover:-translate-y-1 hover:shadow-[0_12px_40px_rgba(0,0,0,0.06)] hover:border-gray-300 transition-all duration-500">
                <div className="h-11 w-11 rounded-xl bg-[#0B63F6]/[0.08] flex items-center justify-center text-[#0B63F6] group-hover:bg-[#0B63F6] group-hover:text-white transition-colors">
                  <DynamicIcon name={s.icon} className="h-5 w-5" />
                </div>
                <h3 className="font-display text-lg font-bold tracking-tight mt-5">{s.title}</h3>
                <p className="text-sm text-gray-600 mt-2 leading-relaxed">{s.short}</p>
                <span className="mt-5 inline-flex items-center gap-1 text-sm font-medium text-[#0B63F6]">Learn more <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" /></span>
              </Link>
            </StaggerItem>
          ))}
        </Stagger>
      </div>
    </section>
  );
}

/* ---------------- Why choose ---------------- */
export function WhyChoose() {
  return (
    <section className="bg-[#FAFAFA] border-y border-gray-100">
      <div className={SECTION}>
        <div className="grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4">
            <SectionHeading eyebrow="Why CompuBrain AI" title="An enterprise partner built for the AI era" />
            <Stagger className="mt-10 grid grid-cols-2 gap-6">
              {STATS.map((s) => (
                <StaggerItem key={s.label}>
                  <div className="font-display text-4xl font-extrabold tracking-tightest text-[#0B63F6]">{s.value}</div>
                  <div className="text-sm text-gray-500 mt-1">{s.label}</div>
                </StaggerItem>
              ))}
            </Stagger>
          </div>
          <div className="lg:col-span-8">
            <Stagger className="grid sm:grid-cols-2 gap-5">
              {WHY_CHOOSE.map((w) => (
                <StaggerItem key={w.title}>
                  <div className="h-full rounded-2xl border border-gray-200 bg-white p-7">
                    <h3 className="font-display text-lg font-bold tracking-tight">{w.title}</h3>
                    <p className="text-[15px] text-gray-600 mt-3 leading-relaxed">{w.body}</p>
                  </div>
                </StaggerItem>
              ))}
            </Stagger>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ---------------- Process (numbered manifesto) ---------------- */
export function Process() {
  return (
    <section>
      <div className={SECTION}>
        <SectionHeading eyebrow="Our Process" title="A structured path to AI visibility" sub="Seven disciplined stages — auditable, repeatable and mapped to business outcomes." className="mb-16" />
        <div className="grid lg:grid-cols-12 gap-8">
          <div className="lg:col-span-12 space-y-0">
            {PROCESS.map((p, i) => (
              <Reveal key={p.step} delay={i * 0.04}>
                <div className="group grid md:grid-cols-12 gap-6 items-start py-8 border-t border-gray-200 hover:bg-[#FAFAFA] transition-colors -mx-4 px-4 rounded-lg">
                  <div className="md:col-span-2">
                    <span className="font-display text-5xl font-black tracking-tightest text-gray-200 group-hover:text-[#0B63F6] transition-colors">{p.step}</span>
                  </div>
                  <div className="md:col-span-4">
                    <h3 className="font-display text-2xl font-bold tracking-tight">{p.title}</h3>
                  </div>
                  <div className="md:col-span-6">
                    <p className="text-gray-600 leading-relaxed">{p.body}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

/* ---------------- Industries ---------------- */
export function IndustriesGrid() {
  return (
    <section className="bg-[#FAFAFA] border-y border-gray-100">
      <div className={SECTION}>
        <div className="flex flex-wrap items-end justify-between gap-6 mb-12">
          <SectionHeading eyebrow="Industries" title="Domain expertise across 16+ industries" />
          <Reveal><Link to="/industries" data-testid="industries-view-all" className="text-sm font-medium text-[#0B63F6] hover:text-[#111111] transition-colors inline-flex items-center gap-1">All industries <ArrowUpRight className="h-4 w-4" /></Link></Reveal>
        </div>
        <Stagger className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
          {INDUSTRIES.map((ind) => (
            <StaggerItem key={ind.slug}>
              <Link to={`/industries/${ind.slug}`} data-testid={`industry-card-${ind.slug}`} className="group flex flex-col gap-3 rounded-2xl border border-gray-200 bg-white p-6 h-full hover:border-[#0B63F6] hover:-translate-y-1 transition-all duration-500">
                <span className="text-[#6E56CF]"><DynamicIcon name={ind.icon} className="h-6 w-6" /></span>
                <span className="font-display font-bold text-[15px] tracking-tight">{ind.title.replace("GEO for ", "").replace("GEO", "GEO")}</span>
              </Link>
            </StaggerItem>
          ))}
        </Stagger>
      </div>
    </section>
  );
}

/* ---------------- Case studies ---------------- */
const CASES = [
  { tag: "Manufacturing", metric: "3.4×", label: "increase in AI citations", body: "An industrial exporter went from invisible to consistently cited by ChatGPT and Perplexity for its product categories after entity and schema work." },
  { tag: "SaaS", metric: "#1", label: "recommended in-category", body: "A B2B SaaS platform became the top brand named by AI assistants when buyers researched its software category." },
  { tag: "Healthcare", metric: "92%", label: "answer accuracy achieved", body: "A multi-speciality provider corrected AI misinformation and reached near-perfect accuracy in how assistants describe its services." },
];
export function CaseStudies() {
  return (
    <section>
      <div className={SECTION}>
        <SectionHeading eyebrow="Case Studies" title="Outcomes that move the business" sub="Representative results from GEO engagements across industries." className="mb-14" />
        <Stagger className="grid md:grid-cols-3 gap-6">
          {CASES.map((c) => (
            <StaggerItem key={c.tag}>
              <div className="h-full rounded-2xl border border-gray-200 p-8 bg-white hover:shadow-[0_12px_40px_rgba(0,0,0,0.05)] transition-all duration-500">
                <span className="text-xs font-bold uppercase tracking-[0.15em] text-[#6E56CF]">{c.tag}</span>
                <div className="font-display text-6xl font-black tracking-tightest text-[#111111] mt-6">{c.metric}</div>
                <div className="text-sm text-gray-500 mt-1">{c.label}</div>
                <p className="text-[15px] text-gray-600 mt-5 leading-relaxed">{c.body}</p>
              </div>
            </StaggerItem>
          ))}
        </Stagger>
      </div>
    </section>
  );
}

/* ---------------- Testimonials ---------------- */
const QUOTES = [
  { quote: "CompuBrain AI made us visible in exactly the AI answers our buyers rely on. Enquiries from AI-driven research are now a real channel.", name: "Marketing Director", role: "Manufacturing Enterprise" },
  { quote: "Finally a partner that understands AI search, not just SEO. The audit alone reshaped our digital strategy.", name: "Founder & CEO", role: "B2B SaaS" },
  { quote: "Transparent, technical and genuinely expert. They corrected years of inconsistent data AI was repeating about us.", name: "Digital Head", role: "Healthcare Group" },
];
export function Testimonials() {
  return (
    <section className="bg-[#111111] text-white">
      <div className={SECTION}>
        <SectionHeading eyebrow="Testimonials" title="Trusted by decision-makers" light className="mb-14" />
        <Stagger className="grid md:grid-cols-3 gap-6">
          {QUOTES.map((q, i) => (
            <StaggerItem key={i}>
              <div className="h-full rounded-2xl border border-white/10 bg-white/[0.03] p-8">
                <Quote className="h-8 w-8 text-[#0B63F6]" />
                <p className="text-[17px] leading-relaxed mt-5 text-white/90">&ldquo;{q.quote}&rdquo;</p>
                <div className="mt-6 pt-6 border-t border-white/10">
                  <div className="font-display font-bold">{q.name}</div>
                  <div className="text-sm text-[#A1A1AA]">{q.role}</div>
                </div>
              </div>
            </StaggerItem>
          ))}
        </Stagger>
      </div>
    </section>
  );
}

/* ---------------- FAQs ---------------- */
export function HomeFaqs() {
  return (
    <section id="faqs">
      <div className={SECTION}>
        <div className="grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4">
            <SectionHeading eyebrow="FAQs" title="Answers about GEO" sub="Everything decision-makers ask before starting a GEO program." />
            <Reveal delay={0.1}>
              <div className="mt-8"><CtaButton source="faqs" title="Ask a GEO Expert" testId="faq-cta">Still have questions?</CtaButton></div>
            </Reveal>
          </div>
          <div className="lg:col-span-8">
            <FaqAccordion faqs={HOME_FAQS} />
          </div>
        </div>
      </div>
    </section>
  );
}
